<script setup lang="ts">
import { Hero, ImageMedia, Tips } from '@clovyr/bed';
import FlowerBg from '@clovyr/bed/assets/images/clovyr-flower-bg.svg';
import { renderMd } from '@clovyr/bed/components/blocks/markdown';
import Markdown from '@clovyr/bed/components/blocks/Markdown.vue';
import PricingBlock from '@clovyr/bed/components/blocks/PricingBlock.vue';
import Tooltip from '@clovyr/bed/components/blocks/Tooltip.vue';
import AppLogo from '@clovyr/bed/components/elements/AppLogo.vue';
import CLink from '@clovyr/bed/components/elements/CLink.vue';
import { useImageAsset } from '@clovyr/bed/composables/useImageAsset';
import { usePrice } from '@clovyr/bed/composables/usePrice';
import { isComingSoon } from '@clovyr/pollen/manifest';
import type { Manifest } from '@clovyr/pollen/manifest/manifest';
import { slugify } from '@clovyr/pollen/util/slugify';

import LaunchCloudName from '@/components/blocks/LaunchCloudName.vue';
import NotifyMeModal from '@/components/blocks/NotifyMeModal.vue';
import Breadcrumbs from '@/components/elements/Breadcrumbs.vue';
import Tag from '@/components/elements/Tag.vue';
import { useAppHead } from '@/composables/useAppHead';
import { useLaunchButton } from '@/composables/useLaunchButton';
import { appURL } from '@/router/util';

import LaunchButton from './AppLauncher/LaunchButton.vue';

const imageAsset = useImageAsset();
const props = defineProps<{
  app: Manifest;
}>();

// FIXME: rename data to metadata
const data = toRef(props.app, 'metadata');
const { handleLaunchClicked } = useLaunchButton(props.app);

const { wnextAppPrice } = usePrice(toRef(props, 'app'));

const appBackground = computed(() => {
  if (!data.value.backgroundImageUrl) {
    return undefined;
  }
  const bg = `url("${imageAsset.getURL(data.value.backgroundImageUrl)}")`;
  return {
    '--bg-url': bg,
  };
});

const logoUrlExists = computed(() => {
  if (data.value.logoUrl && data.value.logoUrl !== '') {
    return true;
  }
  return false;
});

useAppHead(props.app.metadata);

const isAppComingSoon = computed(() => isComingSoon(props.app));
const tags = computed(() => {
  return data.value.tags?.filter((tag) => tag !== 'coming-soon') || [];
});

const showNotifyMeModal = ref(false);

function handleOpenNotifyMeModal() {
  showNotifyMeModal.value = true;
}
function handleCloseNotifyMeModal() {
  showNotifyMeModal.value = false;
}

function scrollToPricing() {
  const pricingElement = document.getElementById('pricing');
  if (pricingElement) {
    pricingElement.scrollIntoView({ behavior: 'smooth' });
  }
}
</script>

<template>
  <div class="app-detail" :style="appBackground">
    <div class="app-detail__top">
      <div class="app-background-gradient"></div>
      <div class="app-background"></div>

      <div class="app-detail-masthead">
        <div class="app-detail-masthead__left">
          <Hero subheadingTextShade="light" class="app-detail__hero">
            <template v-slot:heading> {{ data.name }} </template>

            <template v-slot:subheading>
              {{ data.tagline }}
            </template>

            <template v-slot:top>
              <AppLogo
                class="app-logo-detail"
                size="large"
                :logo="app.metadata"
                v-if="logoUrlExists"
              />
              <AppLogo class="app-logo-detail" size="large" :logo="data" v-else />

              <Breadcrumbs
                :crumbs="[
                  {
                    name: 'garden',
                    routeTo: '/',
                    color: '1',
                  },
                  // TODO: remove temporarily until we have a better way to set this
                  // dynamically. i.e., if landing on detail page via a collection link, include it.
                  // otherwise, hide it? what about tag links?
                  // {
                  //   name: 'bitcoin',
                  //   routeTo: '/collection/bitcoin',
                  //   color: '2',
                  // },
                  {
                    name: data.name,
                  },
                ]"
              />
            </template>

            <template v-slot:right>
              <div class="app-controls button-group">
                <LaunchButton
                  reversed
                  :app="app"
                  :is-coming-soon="isAppComingSoon"
                  @notify-me="handleOpenNotifyMeModal"
                />

                <!-- TODO: bookmark -->
                <!-- <Bookmark /> -->
                <Tooltip
                  v-if="data.id !== 'clovyr-ai-cloud'"
                  content="View pricing details."
                  offset-distance="5"
                >
                  <div v-if="!isAppComingSoon" class="free-trial" @click="scrollToPricing">
                    <span>Enjoy a <span class="trial-text">free</span> 7 day trial on us.</span>
                    Then
                    <span
                      ><span class="trial-text">${{ wnextAppPrice }}</span
                      >/mo</span
                    >.
                  </div>
                </Tooltip>
              </div>
            </template>
          </Hero>
        </div>
      </div>

      <div class="app-hero-image grid">
        <ImageMedia v-if="data.heroImageUrl" :filename="data.heroImageUrl" />
      </div>
    </div>

    <div>
      <Tips v-if="data.huginnSays">
        <template v-slot:heading> huginn says: </template>

        <template v-slot:body>
          <markdown :content="data.huginnSays" />
        </template>
      </Tips>

      <div class="app-info">
        <div class="container container-common">
          <div class="app-info-top row">
            <div class="app-info-top__left grid__col-md-6">
              <div class="app-info__copy">
                <h2 class="app-info__heading" v-if="data.description">Overview</h2>
                <markdown :content="data.description" />
              </div>

              <div class="app-tags" v-if="tags?.length">
                <div class="app-tags__heading">Tagged in:</div>
                <Tag v-for="tag in tags" :key="slugify(tag)" :label="tag" :slug="slugify(tag)" />
              </div>
            </div>

            <div class="app-info-top__right grid__col-md-6" v-if="data.features">
              <div class="app-info__copy features">
                <h2 class="app-info__heading">Features</h2>
                <ul>
                  <li
                    v-for="(feature, index) in data.features"
                    :key="index"
                    v-html="renderMd(feature)"
                  />
                </ul>
              </div>
            </div>
          </div>

          <div class="app-preview" v-if="false && data.media">
            <h3>Previews</h3>
            <div class="app-preview-images">
              <div class="app-preview-image" v-for="(media, index) in data.media" :key="index">
                <ImageMedia :filename="media.url" />
              </div>
            </div>
          </div>

          <div class="app-specs copy">
            <div class="app-specs__row">
              <span class="app-specs__key" v-if="data.website?.text"
                >Website:
                <c-link
                  :to="data.website?.url"
                  :label="data.website.text"
                  target="_blank"
                  class="app-specs__value app-specs__value--link"
                />
              </span>
              <span
                v-if="
                  (data.website?.text && data.version) || (data.website?.text && data.repo?.text)
                "
                class="app-specs__spacer"
                >|</span
              >
              <span class="app-specs__key" v-if="data.repo?.text"
                >Source Code:
                <c-link
                  :to="data.repo?.url"
                  :label="data.repo?.text"
                  target="_blank"
                  class="app-specs__value app-specs__value--link"
                />
              </span>
              <span v-if="data.version && data.repo?.text" class="app-specs__spacer">|</span>
              <span class="app-specs__key" v-if="data.version"
                >Version:
                <span class="app-specs__value">{{ data.version }}</span>
              </span>
              <span v-if="data.version && data.license" class="app-specs__spacer">|</span>
              <span class="app-specs__key" v-if="data.license"
                >License:
                <span class="app-specs__value">{{ data.license }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="app-launch-bar">
        <div class="container container-common">
          <div class="app-launch-bar__left" v-if="data.name">
            <LaunchCloudName :appName="data.name" />
          </div>

          <div class="app-launch-bar__right">
            <LaunchButton
              reversed
              :app="app"
              :is-coming-soon="isAppComingSoon"
              @notify-me="handleOpenNotifyMeModal"
            />

            <!-- <Bookmark /> -->
          </div>
        </div>
      </div>
    </div>
    <router-view :parentRoute="appURL(app)"> </router-view>
  </div>

  <div id="pricing">
    <PricingBlock
      v-if="data.id !== 'clovyr-ai-cloud'"
      @launch="handleLaunchClicked"
      :image="FlowerBg"
      :app="app"
      :is-coming-soon="isAppComingSoon"
      @notify-me="handleOpenNotifyMeModal"
    />
  </div>
  <NotifyMeModal
    v-if="showNotifyMeModal"
    :appName="props.app.metadata.name"
    :appId="props.app.metadata.id"
    @close="handleCloseNotifyMeModal"
  />
</template>

<style lang="scss" scoped>
@use 'sass:color';
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-utils';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/typography/fonts';
@use 'styles/core/typography/type';
@use 'styles/core/util';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

:global(html) {
  scroll-behavior: smooth;
}

.app-specs__value {
  padding: 0 0 0 5px;
}

.app-detail {
  :deep(.hero) {
    .hero__inner {
      margin-bottom: grid-vars.space(5);
      padding-top: grid-vars.space(12);

      display: flex;
      flex-direction: column;

      @include breakpoints.media-breakpoint-up(md) {
        margin-bottom: grid-vars.space(9);
        flex-direction: row;
        align-items: flex-end;
        justify-content: space-between;
      }

      .hero__middle {
        margin-bottom: 0;

        .hero__heading {
          @include fonts.font-size(32);

          @include breakpoints.media-breakpoint-up(md) {
            @include fonts.font-size(42);
          }

          @include breakpoints.media-breakpoint-up(xl) {
            @include fonts.font-size(67);
          }

          margin-bottom: 0;
        }
      }
    }
  }

  .app-background-gradient {
    position: absolute;
    top: 0;
    left: 0;
    z-index: -4;
    width: 100%;
    height: grid-vars.space(60);
    background: linear-gradient(180deg, rgb(20 20 20 / 80%) 0%, colors.color(negative) 100%);
  }

  .app-background {
    position: absolute;
    top: 0;
    left: 0;
    z-index: -5;
    width: 100%;
    height: grid-vars.space(60);
    background: var(--bg-url);

    @include util.background-image;
  }

  .app-logo-detail {
    margin-bottom: grid-vars.space(3.7);
  }

  .app-controls {
    margin-top: grid-vars.space(3);
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    @include breakpoints.media-breakpoint-up(md) {
      align-items: center;
      margin-top: 20%;
    }

    .free-trial {
      cursor: pointer;
      margin-top: 0.5rem;
      .trial-text {
        color: colors.color(collection-1, text-primary);
        font-weight: 800;
      }
    }
  }

  .app-hero-image {
    display: flex;
    justify-content: space-around;

    @include breakpoints.media-breakpoint-down(md) {
      padding-right: 0;
      padding-left: 0;
    }

    :deep(.image-media) {
      box-shadow: grid-vars.space(1.2) grid-vars.space(1.2) grid-vars.space(20) 0
        color.adjust(colors.color(black), $alpha: -0.5);
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
      display: flex;
      justify-content: center;
      max-width: grid-vars.space(140);
      overflow: hidden;
      width: 80%;

      img {
        width: 100%;
      }
    }

    iframe {
      @include util.borders-rounded;

      height: grid-vars.space(69.4);
      padding: grid-vars.space(4) var(--col-gutter-outer-left) grid-vars.space(2)
        var(--col-gutter-outer-right);

      @include breakpoints.media-breakpoint-down(md) {
        padding: 0 calc(var(--col-gutter-outer-left) + #{grid-utils.col-width(0.5)})
          grid-vars.space(11);
      }
    }
  }

  .app-info {
    color: colors.color(grey, tertiary);
    background-color: color.adjust(colors.color(background, dark-navy), $alpha: -0.5);
    width: fit-content;

    &__copy {
      @include type.copy;

      @include fonts.font-size(16);

      color: colors.color(white);
      line-height: 1.4;

      li {
        list-style: disc;
        &::before {
          content: '';
        }
      }

      p {
        margin-top: grid-vars.space(4);
      }
    }

    &__heading {
      @include fonts.font-size(26);

      color: colors.color(white);
      font-weight: 500;
      letter-spacing: -0.03rem;
    }
  }

  .app-info-top {
    padding-top: grid-vars.space(4);
    padding-bottom: grid-vars.space(4);
    flex-direction: column-reverse;

    @include breakpoints.media-breakpoint-up(md) {
      padding-bottom: grid-vars.space(8);
      flex-direction: row;
    }

    .app-info-top__right {
      margin-top: grid-vars.space(3);
      margin-bottom: grid-vars.space(5);

      @include breakpoints.media-breakpoint-up(md) {
        margin-top: 0;
        margin-bottom: 0;
        display: flex;
        justify-content: space-evenly;
      }
    }
  }

  .app-tags {
    margin-top: grid-vars.space(3);

    &__heading {
      display: inline-block;
      margin-right: grid-vars.space(0.8);
    }
  }

  .app-preview-images {
    .app-preview-image {
      margin: grid-vars.space(2) 0 grid-vars.space(3) 0;
    }
  }

  .app-specs {
    &__row:not(:last-child) {
      margin-bottom: grid-vars.space(0.8);
    }

    &__key {
      display: inline-block;
      margin-right: grid-vars.space(0.8);
    }

    &__value {
      @include type.text--mono;

      display: inline-block;

      &--link {
        color: colors.color(action, primary);
        text-decoration: none;
        text-transform: uppercase;
      }
    }

    &__spacer {
      margin: 0 grid-vars.space(2.2);
    }
  }

  .app-launch-bar {
    padding-top: 5rem;
    padding-bottom: 5rem;
    background-color: color.adjust(colors.color(background, dark-navy), $alpha: -0.5);

    @include breakpoints.media-breakpoint-up(md) {
      padding-top: grid-vars.space(8);
    }

    .container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      @include breakpoints.media-breakpoint-up(lg) {
        flex-direction: row;
        justify-content: space-between;
        align-items: baseline;
      }
    }

    &__right {
      display: flex;
      align-items: flex-end;

      @include breakpoints.media-breakpoint-up(sm) {
        justify-content: flex-end;
      }
    }
    &__left {
      min-height: 6rem;
    }
  }
}
</style>
